import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Globe2, Check } from "lucide-react";

const COMMON_ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC"];

export function allZones() {
  try { return Intl.supportedValuesOf("timeZone"); } catch { return COMMON_ZONES; }
}

export function getSavedZone(user) {
  return user?.preferences?.timezone || localStorage.getItem("cocoa_tz") || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function saveZone(tz) {
  localStorage.setItem("cocoa_tz", tz);
  window.dispatchEvent(new CustomEvent("cocoa-tz-changed", { detail: tz }));
  api.put("/me/preferences", { timezone: tz }).catch(() => {});
}

export function HeaderClock() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [tz, setTz] = useState(() => getSavedZone(user));
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (user?.preferences?.timezone) setTz(user.preferences.timezone); }, [user]);
  useEffect(() => {
    const h = (e) => setTz(e.detail);
    window.addEventListener("cocoa-tz-changed", h);
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { window.removeEventListener("cocoa-tz-changed", h); document.removeEventListener("mousedown", close); };
  }, []);

  let dateStr = "", timeStr = "", abbr = "";
  try {
    dateStr = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(now);
    timeStr = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(now);
    abbr = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(now).find((p) => p.type === "timeZoneName")?.value || "";
  } catch { /* invalid tz */ }

  const zones = [...new Set([...COMMON_ZONES, ...allZones()])].filter((z) => z.toLowerCase().includes(filter.toLowerCase())).slice(0, 40);

  return (
    <div className="relative hidden lg:block" ref={ref}>
      <button data-testid="header-clock-btn" onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-left hover:bg-white/20 transition-colors">
        <Globe2 size={14} className="text-white/60" />
        <span>
          <span className="block text-xs font-semibold leading-tight tabular-nums" data-testid="header-clock-time">{timeStr} {abbr}</span>
          <span className="block text-[10px] text-white/60 leading-tight" data-testid="header-clock-date">{dateStr}</span>
        </span>
      </button>
      {open && (
        <div data-testid="timezone-dropdown" className="absolute right-0 top-12 z-[95] w-72 rounded-2xl border border-[#EFEBE0] bg-white text-[#3E2723] shadow-[0_16px_48px_-12px_rgba(62,39,35,0.35)] py-2">
          <div className="px-3 pb-2">
            <input data-testid="timezone-filter-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search timezones"
              className="h-9 w-full rounded-lg border border-[#E5DFD3] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {zones.map((z) => (
              <button key={z} data-testid={`tz-option-${z.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                onClick={() => { setTz(z); saveZone(z); setOpen(false); }}
                className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-[#F5F2EB]">
                <span className="truncate">{z.replace(/_/g, " ")}</span>
                {z === tz && <Check size={14} className="text-[#2E7D32] shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
